import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  BulkResult,
  BulkTask,
  DocumentStatus,
  EtaStatus,
  Readiness,
  SalesDocumentSummary,
} from '../../../api/types';
import { BATCH_SESSION_PREFIX, readSession, writeSession } from '../../../lib/storage';

/**
 * The named batch of the batch page (WP-1b.3, BLOCK-1B-BULK-DESIGN §3 "Keeping"): the task, up to
 * 100 documents, the latest review and the latest run, kept for the browser tab, per organization
 * and user (`sessionStorage`). Everything here is pure except the hook at the end; every storage
 * access goes through `lib/storage` (try/catch): without storage the batch lives for the page only.
 */

export const BATCH_MAX = 100;
export const BATCH_TASKS: readonly BulkTask[] = ['send', 'pdf', 'discard'];

export interface BatchItem {
  id: string;
  number: string | null;
  receiverName: string | null;
  cash: boolean;
  totalAmount: string | null;
  status: DocumentStatus;
  etaStatus: EtaStatus | null;
  readiness: Readiness | null;
  version: number;
}

/** The latest dry run, for one task and one list of documents (in batch order). */
export interface BatchReview {
  task: BulkTask;
  ids: string[];
  results: BulkResult[];
}

/** The latest run and its results (one row per document, in the call's order). */
export interface BatchRun {
  task: BulkTask;
  ids: string[];
  /** `running`: the call was sent and has not answered; `file`: PDFs are being fetched. */
  phase: 'running' | 'file' | 'done';
  results: BulkResult[];
  /** The order of the results table, fixed when the run ends (§5). */
  order: string[];
  /** The page lost the call (left or reloaded during the run): rows show each document now. */
  lost?: boolean;
  startedAt: string;
}

export interface BatchState {
  v: 1;
  task: BulkTask | null;
  items: BatchItem[];
  review: BatchReview | null;
  run: BatchRun | null;
}

export const emptyBatch = (): BatchState => ({
  v: 1,
  task: null,
  items: [],
  review: null,
  run: null,
});

export const batchKey = (orgId: string, userId: string) =>
  `${BATCH_SESSION_PREFIX}v1.${orgId}.${userId}`;

/** A list row (or a bulk result) as a batch item. */
export function itemOf(doc: SalesDocumentSummary | (BulkResult & { id: string })): BatchItem {
  return {
    id: doc.id,
    number: doc.number ?? null,
    receiverName: doc.receiverName ?? null,
    cash: doc.cash ?? false,
    totalAmount: doc.totalAmount ?? null,
    status: (doc.status ?? 'draft') as DocumentStatus,
    etaStatus: (doc.etaStatus ?? null) as EtaStatus | null,
    readiness: (doc.readiness ?? null) as Readiness | null,
    version: doc.version ?? 1,
  };
}

/**
 * Adds documents in the given order, skipping those already in the batch, until it holds 100.
 * `added` = how many went in; `left` = how many new ones did not fit.
 */
export function addItems(
  state: BatchState,
  docs: readonly BatchItem[],
): { state: BatchState; added: number; left: number } {
  const have = new Set(state.items.map((i) => i.id));
  const items = [...state.items];
  let added = 0;
  let left = 0;
  for (const doc of docs) {
    if (have.has(doc.id)) continue;
    have.add(doc.id);
    if (items.length >= BATCH_MAX) {
      left += 1;
      continue;
    }
    items.push(doc);
    added += 1;
  }
  if (!added) return { state, added, left };
  return { state: { ...state, items, review: null }, added, left };
}

/** No room left: put-all adds nothing and the add field is disabled (`batch.full`). */
export const isFull = (state: BatchState) => state.items.length >= BATCH_MAX;

/** Takes documents out of the batch («أخرِج», «أخرِج ما سيُتخطّى»); no document changes. */
export function removeItems(state: BatchState, ids: readonly string[]): BatchState {
  const out = new Set(ids);
  const items = state.items.filter((i) => !out.has(i.id));
  if (items.length === state.items.length) return state;
  return { ...state, items, review: null };
}

/** «إفراغ الحزمة»: every document leaves; the task stays. */
export const clearItems = (state: BatchState): BatchState => ({
  ...state,
  items: [],
  review: null,
  run: null,
});

/** A new task keeps the batch and always needs a fresh review (§2). */
export function setTask(state: BatchState, task: BulkTask): BatchState {
  if (state.task === task) return state;
  return { ...state, task, review: null, run: null };
}

/** Refreshes the items from the documents' latest reading (a dry run or a re-read). */
export function refreshItems(state: BatchState, docs: readonly BulkResult[]): BatchState {
  const byId = new Map(docs.filter((d) => d.status).map((d) => [d.id, d]));
  if (!byId.size) return state;
  const items = state.items.map((item) => {
    const doc = byId.get(item.id);
    return doc ? { ...itemOf(doc), number: doc.number ?? item.number } : item;
  });
  return { ...state, items };
}

/** Is the review the one for the batch as it is now (same task, same documents, same order)? */
export function reviewIsCurrent(state: BatchState): boolean {
  const r = state.review;
  if (!r || r.task !== state.task) return false;
  return r.ids.length === state.items.length && r.ids.every((id, i) => state.items[i]?.id === id);
}

// ------------------------------------------------------------------------------------- storage
const STATUSES = new Set(['draft', 'issued', 'signing', 'signed', 'superseded', 'discarded']);
const isText = (v: unknown): v is string => typeof v === 'string';
const isTextOrNull = (v: unknown) => v === null || typeof v === 'string';

function parseItem(raw: unknown): BatchItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!isText(r.id) || !isText(r.status) || !STATUSES.has(r.status)) return null;
  if (!isTextOrNull(r.number) || !isTextOrNull(r.receiverName) || !isTextOrNull(r.totalAmount))
    return null;
  if (typeof r.version !== 'number' || typeof r.cash !== 'boolean') return null;
  return {
    id: r.id,
    number: r.number as string | null,
    receiverName: r.receiverName as string | null,
    cash: r.cash,
    totalAmount: r.totalAmount as string | null,
    status: r.status as DocumentStatus,
    etaStatus: (isText(r.etaStatus) ? r.etaStatus : null) as EtaStatus | null,
    readiness: (isText(r.readiness) ? r.readiness : null) as Readiness | null,
    version: r.version,
  };
}

const isResults = (v: unknown): v is BulkResult[] =>
  Array.isArray(v) &&
  v.every((x) => x && typeof x === 'object' && isText((x as BulkResult).id) && 'ok' in x);
const isIds = (v: unknown): v is string[] => Array.isArray(v) && v.every(isText);
const isTask = (v: unknown): v is BulkTask =>
  isText(v) && (BATCH_TASKS as readonly string[]).includes(v);

/** Reads a stored batch. Unreadable data is dropped; more than 100 items are cut to 100. */
export function parseBatch(text: string | null): BatchState {
  if (!text) return emptyBatch();
  try {
    const raw = JSON.parse(text) as Record<string, unknown>;
    if (!raw || raw.v !== 1 || !Array.isArray(raw.items)) return emptyBatch();
    if (raw.task !== null && !isTask(raw.task)) return emptyBatch();
    const items: BatchItem[] = [];
    const seen = new Set<string>();
    for (const value of raw.items) {
      const item = parseItem(value);
      if (!item) return emptyBatch();
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
    const review = raw.review as BatchReview | null;
    const run = raw.run as BatchRun | null;
    return {
      v: 1,
      task: raw.task as BulkTask | null,
      items: items.slice(0, BATCH_MAX),
      review:
        review && isTask(review.task) && isIds(review.ids) && isResults(review.results)
          ? review
          : null,
      run:
        run &&
        isTask(run.task) &&
        isIds(run.ids) &&
        isIds(run.order) &&
        isResults(run.results) &&
        ['running', 'file', 'done'].includes(run.phase)
          ? run
          : null,
    };
  } catch {
    return emptyBatch();
  }
}

export const loadBatch = (orgId: string, userId: string): BatchState =>
  parseBatch(readSession(batchKey(orgId, userId)));

export function saveBatch(orgId: string, userId: string, state: BatchState): void {
  writeSession(batchKey(orgId, userId), JSON.stringify(state));
}

export const forgetBatch = (orgId: string, userId: string) =>
  writeSession(batchKey(orgId, userId), null);

/**
 * The batch of this organization and user, kept for the tab: every update is computed from the
 * latest state at once and written at once (a later read in another page of the same tab sees it).
 * The getter returns that latest state between renders (async steps of a run).
 */
export function useBatch(
  orgId: string,
  userId: string,
): [BatchState, (update: (state: BatchState) => BatchState) => void, () => BatchState] {
  const [state, setState] = useState<BatchState>(() => loadBatch(orgId, userId));
  const latest = useRef(state);
  const key = batchKey(orgId, userId);
  const loadedKey = useRef(key);
  useEffect(() => {
    if (loadedKey.current === key) return;
    loadedKey.current = key;
    latest.current = loadBatch(orgId, userId);
    setState(latest.current);
  }, [key, orgId, userId]);
  const update = useCallback(
    (fn: (state: BatchState) => BatchState) => {
      const next = fn(latest.current);
      if (next === latest.current) return;
      latest.current = next;
      saveBatch(orgId, userId, next);
      setState(next);
    },
    [orgId, userId],
  );
  const get = useCallback(() => latest.current, []);
  return [state, update, get];
}
